import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
// Los estilos CSS de react-pdf ahora se importan en index.html
// import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
// import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configura el worker de react-pdf para que cargue desde tu propia ruta local.
// Asegúrate de haber colocado 'pdf.worker.min.mjs' en tu carpeta 'public'.
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'; // ¡RUTA ABSOLUTA!

// --- IMPORTANTE: APUNTAMOS AL PROXY DE VITE EN LUGAR DE LA URL DIRECTA DE APPS SCRIPT ---
// Vite redirigirá esta solicitud a tu Apps Script.
const APPS_SCRIPT_WEB_APP_URL = 'https://script.google.com/macros/s/AKfycbwDSYrMl5ypAPTB_iiqQdBtScKH6my6xUGV99yPCXZgf4RbFfvg5wqxWCOEQYjcxLTz/exec'; // ¡DEBE APUNTAR AL PROXY LOCAL!

// Tamaño del chunk de páginas a cargar a la vez
const PAGE_LOAD_CHUNK_SIZE = 25;

function App() {
  const [currentPage, setCurrentPage] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [ebooks, setEbooks] = useState([]);
  const [selectedEbook, setSelectedEbook] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);

  // --- Nuevos estados para el control del PDF ---
  const [pdfScale, setPdfScale] = useState(1.0);
  const [pdfOriginalWidth, setPdfOriginalWidth] = useState(null);
  const [pdfOriginalHeight, setPdfOriginalHeight] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const viewerRef = useRef(null);
  const pageRefs = useRef([]);
  const [pagesRenderedCount, setPagesRenderedCount] = useState(PAGE_LOAD_CHUNK_SIZE);

  /**
   * Función asíncna para realizar llamadas a la API de Google Apps Script.
   * Ahora envía datos como 'application/x-www-form-urlencoded'.
   * @param {string} action - La acción que se desea realizar en el backend.
   * @param {Object} data - Un objeto con los datos a enviar al backend.
   * @returns {Promise<Object>} - Una promesa que resuelve con la respuesta JSON del servidor.
   */
  const callAppsScript = async (action, data = {}) => {
    setMessage('');
    try {
      // Convertir el objeto de datos a formato URL-encoded
      const formData = new URLSearchParams();
      formData.append('action', action); // Añadir la acción como un parámetro más
      for (const key in data) {
        formData.append(key, data[key]);
      }

      const response = await fetch(APPS_SCRIPT_WEB_APP_URL, {
        method: 'POST',
        // ¡IMPORTANTE! Cambiar Content-Type y body
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(), // Enviar como string URL-encoded
      });

      if (!response.ok) {
        console.error(`Error HTTP: ${response.status} ${response.statusText}`);
        const errorBody = await response.text();
        console.error('Cuerpo del error de respuesta:', errorBody);
        setMessage(`Error de conexión: ${response.status} ${response.statusText || 'Error desconocido'}`);
        return { success: false, message: `Error de conexión: ${response.status} ${response.statusText || 'Error desconocido'}` };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error al llamar a Apps Script:', error);
      setMessage('Error de conexión con el servidor. Inténtelo de nuevo.');
      return { success: false, message: 'Error de conexión.' };
    }
  };

  const handleLogin = async () => {
    if (!username || !password) {
      setMessage('Por favor, ingrese usuario y contraseña.');
      return;
    }
    setMessage('Iniciando sesión...');
    const result = await callAppsScript('login', { username, password });
    if (result.success) {
      setCurrentUser(result.user);
      setMessage(result.message);
      fetchEbooks();
      setCurrentPage('ebookList');
    } else {
      setMessage(result.message);
    }
  };

  const handleCreateUser = async () => {
    if (!username || !email || !password) {
      setMessage('Por favor, complete todos los campos.');
      return;
    }
    setMessage('Creando usuario...');
    const result = await callAppsScript('createUser', { username, email, password });
    if (result.success) {
      setMessage(result.message + ' Ahora puede iniciar sesión.');
      setCurrentPage('login');
      setUsername('');
      setEmail('');
      setPassword('');
    } else {
      setMessage(result.message);
    }
  };

  const fetchEbooks = async () => {
    setMessage('Cargando Ebooks...');
    const result = await callAppsScript('getEbooks');
    if (result.success) {
      setEbooks(result.ebooks);
      setMessage('');
    } else {
      setMessage(result.message);
    }
  };

  const handleSelectEbook = (ebook) => {
    setSelectedEbook(ebook);
    setCurrentPage('ebookDetail');
  };

  const handleReadEbook = () => {
    if (currentUser && currentUser.subscriptionActive) {
      setCurrentPage('pdfViewer');
      setPdfLoading(true);
      setPageNumber(1);
      setPdfScale(1.0);
      setPdfOriginalWidth(null);
      setPdfOriginalHeight(null);
      pageRefs.current = [];
      setPagesRenderedCount(PAGE_LOAD_CHUNK_SIZE);
    } else {
      setMessage('No hay una suscripción Activa.');
    }
  };

  const handleFitToWidth = () => {
    if (viewerRef.current && pdfOriginalWidth) {
      const availableWidth = viewerRef.current.clientWidth;
      setPdfScale(availableWidth / pdfOriginalWidth);
    }
  };

  const handleFitToHeight = () => {
    if (viewerRef.current && pdfOriginalHeight) {
      const availableHeight = isFullScreen ? window.innerHeight * 0.95 : viewerRef.current.clientHeight * 0.9;
      setPdfScale(availableHeight / pdfOriginalHeight);
    }
  };

  const handleActualSize = () => {
    setPdfScale(1.0);
  };

  const handleZoomIn = () => {
    setPdfScale(prevScale => prevScale + 0.1);
  };

  const handleZoomOut = () => {
    setPdfScale(prevScale => Math.max(0.5, prevScale - 0.1));
  };

  const goToPrevPage = () => {
    setPageNumber(prevPageNumber => {
      const newPage = Math.max(prevPageNumber - 1, 1);
      if (pageRefs.current[newPage - 1]) {
        pageRefs.current[newPage - 1].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return newPage;
    });
  };

  const goToNextPage = () => {
    setPageNumber(prevPageNumber => {
      const newPage = Math.min(prevPageNumber + 1, numPages);
      if (pageRefs.current[newPage - 1]) {
        pageRefs.current[newPage - 1].scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (newPage > pagesRenderedCount) {
        loadMorePages();
        setTimeout(() => {
          if (pageRefs.current[newPage - 1]) {
            pageRefs.current[newPage - 1].scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
      return newPage;
    });
  };

  const loadMorePages = () => {
    setPagesRenderedCount(prevCount => Math.min(numPages, prevCount + PAGE_LOAD_CHUNK_SIZE));
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      if (viewerRef.current) {
        viewerRef.current.requestFullscreen().catch(err => {
          console.error(`Error al intentar pantalla completa: ${err.message}`);
          setMessage('No se pudo activar la pantalla completa.');
        });
      }
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
      if (pdfOriginalWidth && pdfOriginalHeight) {
        const currentIsFullScreen = !!document.fullscreenElement;
        if (pdfScale === viewerRef.current.clientWidth / pdfOriginalWidth) {
          handleFitToWidth();
        } else if (pdfScale === (currentIsFullScreen ? window.innerHeight * 0.95 : viewerRef.current.clientHeight * 0.9) / pdfOriginalHeight) {
          handleFitToHeight();
        }
      }
    };
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, [pdfScale, pdfOriginalWidth, pdfOriginalHeight, isFullScreen]);

  useEffect(() => {
    const handleResize = () => {
      if (currentPage === 'pdfViewer' && viewerRef.current && pdfOriginalWidth && pdfOriginalHeight) {
        if (pdfScale === viewerRef.current.clientWidth / pdfOriginalWidth) {
          handleFitToWidth();
        } else if (pdfScale === (isFullScreen ? window.innerHeight * 0.95 : viewerRef.current.clientHeight * 0.9) / pdfOriginalHeight) {
          handleFitToHeight();
        }
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentPage, pdfScale, pdfOriginalWidth, pdfOriginalHeight, isFullScreen]);

  useEffect(() => {
    const handleScroll = () => {
      if (!viewerRef.current || !numPages || pageRefs.current.length === 0) return;

      const viewerScrollTop = viewerRef.current.scrollTop;
      const viewerHeight = viewerRef.current.clientHeight;

      let foundPage = 1;
      for (let i = 0; i < numPages; i++) {
        const pageElement = pageRefs.current[i];
        if (pageElement) {
          const pageTop = pageElement.offsetTop;
          const pageBottom = pageTop + pageElement.clientHeight;

          if (pageTop <= viewerScrollTop + viewerHeight / 2 && pageBottom >= viewerScrollTop + viewerHeight / 2) {
            foundPage = i + 1;
            break;
          }
        }
      }
      if (foundPage !== pageNumber) {
        setPageNumber(foundPage);
      }
    };

    const viewerElement = viewerRef.current;
    if (viewerElement) {
      viewerElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (viewerElement) {
        viewerElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [numPages, viewerRef.current, pdfScale, pageNumber]);


  const onDocumentLoadSuccess = (pdf) => {
    setNumPages(pdf.numPages);
    setPdfLoading(false);

    pdf.getPage(1).then(page => {
      const viewport = page.getViewport({ scale: 1 });
      setPdfOriginalWidth(viewport.width);
      setPdfOriginalHeight(viewport.height);

      setTimeout(() => {
        if (viewerRef.current) {
          handleFitToWidth();
        }
      }, 100);
    }).catch(error => {
      console.error("Error al obtener dimensiones de la página PDF:", error);
      setMessage("Error al obtener dimensiones del PDF.");
      setPdfLoading(false);
    });
  };

  const onDocumentLoadError = (error) => {
    console.error('Error al cargar PDF:', error);
    setMessage('Error al cargar el PDF. Asegúrese de que la URL sea correcta y el archivo exista.');
    setPdfLoading(false);
  };

  return (
    <div className="font-sans antialiased">
      {(() => {
        switch (currentPage) {
          case 'login':
            return (
              <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4 font-sans">
                <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md">
                  <img src="https://ecommercelatinoamerica.com/biblia/BIBLIACOMIC.jpg" alt="La Biblia en Comic" className="w-full h-auto rounded-lg mb-6" />
                  <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Iniciar Sesión</h2>
                  <input
                    type="text"
                    placeholder="Usuario"
                    className="w-full p-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                  <input
                    type="password"
                    placeholder="Contraseña"
                    className="w-full p-3 mb-6 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    onClick={handleLogin}
                    className="w-full bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-300 shadow-md"
                  >
                    Ingresar
                  </button>
                  <button
                    onClick={() => setCurrentPage('createUser')}
                    className="w-full mt-4 bg-gray-200 text-gray-800 p-3 rounded-lg font-semibold hover:bg-gray-300 transition duration-300 shadow-md"
                  >
                    Crear Usuario
                  </button>
                  {message && <p className="mt-4 text-center text-red-500 text-sm">{message}</p>}
                </div>
              </div>
            );
          case 'createUser':
            return (
              <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4 font-sans">
                <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md">
                  <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Crear Nuevo Usuario</h2>
                  <input
                    type="text"
                    placeholder="Nombre de Usuario"
                    className="w-full p-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                  <input
                    type="email"
                    placeholder="Correo Electrónico"
                    className="w-full p-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <input
                    type="password"
                    placeholder="Contraseña"
                    className="w-full p-3 mb-6 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    onClick={handleCreateUser}
                    className="w-full bg-green-600 text-white p-3 rounded-lg font-semibold hover:bg-green-700 transition duration-300 shadow-md"
                  >
                    Registrarse
                  </button>
                  <button
                    onClick={() => setCurrentPage('login')}
                    className="w-full mt-4 bg-gray-200 text-gray-800 p-3 rounded-lg font-semibold hover:bg-gray-300 transition duration-300 shadow-md"
                  >
                    Volver al Login
                  </button>
                  {message && <p className="mt-4 text-center text-red-500 text-sm">{message}</p>}
                </div>
              </div>
            );
          case 'ebookList':
            return (
              <div className="min-h-screen bg-gray-100 p-4 font-sans">
                <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-4 rounded-xl shadow-md mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 sm:mb-0">Ebooks Disponibles</h2>
                  {currentUser && (
                    <div className="text-sm text-gray-600 flex flex-col sm:flex-row items-center mb-4 sm:mb-0">
                      <span className="mb-2 sm:mb-0 sm:mr-2">Bienvenido, <span className="font-semibold">{currentUser.username}</span></span>
                      {currentUser.subscriptionActive ? (
                        <span className="px-2 py-1 bg-green-200 text-green-800 rounded-full text-xs font-medium">Suscripción Activa</span>
                      ) : (
                        <span className="px-2 py-1 bg-red-200 text-red-800 rounded-full text-xs font-medium">Suscripción Inactiva</span>
                      )}
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setCurrentUser(null);
                      setCurrentPage('login');
                      setUsername('');
                      setPassword('');
                      setEmail('');
                      setMessage('');
                      setEbooks([]);
                      setSelectedEbook(null);
                    }}
                    className="bg-red-500 text-white p-2 rounded-lg font-semibold hover:bg-red-600 transition duration-300 shadow-md"
                  >
                    Cerrar Sesión
                  </button>
                </div>

                {message && <p className="text-center text-blue-500 mb-4">{message}</p>}

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {ebooks.length > 0 ? (
                    ebooks.map((ebook) => (
                      <div
                        key={ebook.ID}
                        className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transform hover:scale-105 transition duration-300"
                        onClick={() => handleSelectEbook(ebook)}
                      >
                        <img
                          src={ebook.Imagen}
                          alt={ebook.NombreEbook}
                          className="w-full h-48 object-cover rounded-t-xl"
                          onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/400x300/CCCCCC/333333?text=No+Imagen`; }}
                        />
                        <div className="p-4">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">{ebook.NombreEbook}</h3>
                          <p className="text-sm text-gray-600">Autor: {ebook.Autor}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="col-span-full text-center text-gray-600">No hay ebooks disponibles en este momento.</p>
                  )}
                </div>
              </div>
            );
          case 'ebookDetail':
            if (!selectedEbook) return null;
            return (
              <div className="flex flex-col items-center min-h-screen bg-gray-100 p-4 font-sans">
                <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-2xl">
                  <button
                    onClick={() => {
                      setCurrentPage('ebookList');
                      setMessage('');
                    }}
                    className="mb-6 bg-gray-200 text-gray-800 p-2 rounded-lg font-semibold hover:bg-gray-300 transition duration-300 shadow-md"
                  >
                    &larr; Volver a Ebooks
                  </button>
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                    <img
                      src={selectedEbook.Imagen}
                      alt={selectedEbook.NombreEbook}
                      className="w-full md:w-1/3 h-auto object-cover rounded-lg shadow-md"
                      onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/400x600/CCCCCC/333333?text=No+Imagen`; }}
                    />
                    <div className="flex-1">
                      <h2 className="text-4xl font-bold text-gray-900 mb-4">{selectedEbook.NombreEbook}</h2>
                      <p className="text-lg text-gray-700 mb-2"><strong>Autor:</strong> {selectedEbook.Autor}</p>
                      <p className="text-lg text-gray-700 mb-2"><strong>Año:</strong> {selectedEbook.Año}</p>
                      <p className="text-lg text-gray-700 mb-4"><strong>Rating:</strong> {selectedEbook.Rating} / 5</p>

                      <button
                        onClick={handleReadEbook}
                        className="w-full bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-300 shadow-md mb-4"
                      >
                        Leer Digitalmente
                      </button>

                      {message && <p className="text-red-500 text-center mb-4">{message}</p>}

                      {!currentUser?.subscriptionActive && message.includes('No hay una suscripción Activa') && (
                        <a
                          href="http://wa.me/50253714553"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full bg-green-500 text-white p-3 rounded-lg font-semibold text-center block hover:bg-green-600 transition duration-300 shadow-md"
                        >
                          Activar Suscripción (WhatsApp)
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          case 'pdfViewer':
            if (!selectedEbook || !currentUser?.subscriptionActive) {
              setCurrentPage('ebookList');
              setMessage('Acceso denegado. No hay suscripción activa o ebook no seleccionado.');
              return null;
            }
            return (
              // Contenedor principal del visor de PDF
              // En móvil, p-0 elimina el padding lateral para ocupar todo el ancho
              // En fullscreen, fixed inset-0 para ocupar toda la pantalla
              <div ref={viewerRef} className={`flex flex-col items-center min-h-screen bg-gray-100 p-0 font-sans ${isFullScreen ? 'fixed inset-0 z-50 bg-black' : ''}`}>
                {/* Contenedor del contenido (fondo blanco, sombra, etc.) */}
                {/* En móvil, rounded-none y shadow-none para ocupar todo el ancho */}
                {/* En fullscreen, max-w-full y h-full para ocupar toda la pantalla */}
                <div className={`bg-white rounded-none shadow-none w-full ${isFullScreen ? 'max-w-full h-full flex flex-col' : 'max-w-full sm:max-w-4xl p-0 sm:p-8 rounded-xl shadow-2xl'}`}>
                  
                  {/* Controles superiores (Volver, Título) - Ocultos en Fullscreen */}
                  {!isFullScreen && (
                    <div className="flex justify-between items-center px-4 sm:px-0 mb-4 sm:mb-6 flex-wrap">
                      <button
                        onClick={() => {
                          setCurrentPage('ebookList');
                          setNumPages(null);
                          setPageNumber(1);
                          setPdfLoading(false);
                          setIsFullScreen(false);
                        }}
                        className="bg-gray-200 text-gray-800 p-2 rounded-lg font-semibold hover:bg-gray-300 transition duration-300 shadow-md mb-2 sm:mb-0"
                      >
                        &larr; Volver a Ebooks
                      </button>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 text-center flex-grow sm:flex-grow-0">{selectedEbook.NombreEbook}</h2>
                      <div className="w-24 sm:w-auto"></div> {/* Espacio para alinear */}
                    </div>
                  )}

                  {/* Indicador de carga */}
                  {pdfLoading && (
                    <div className={`flex flex-col justify-center items-center ${isFullScreen ? 'flex-grow' : 'h-64'}`}>
                      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
                      <p className="ml-4 text-lg text-gray-600 mt-4">Cargando PDF...</p>
                    </div>
                  )}

                  {/* Controles de visualización (Zoom, Ajuste, Pantalla Completa) - Ocultos en Fullscreen */}
                  {!isFullScreen && (
                    <div className="flex justify-center items-center flex-wrap gap-2 mb-4 px-4 sm:px-0">
                      <button onClick={handleFitToWidth} className="bg-blue-500 text-white p-2 rounded-lg text-sm hover:bg-blue-600 transition duration-300 shadow-md">
                        Ancho Completo
                      </button>
                      <button onClick={handleFitToHeight} disabled={!pdfOriginalHeight} className="bg-blue-500 text-white p-2 rounded-lg text-sm hover:bg-blue-600 transition duration-300 shadow-md disabled:opacity-50">
                        Alto Completo
                      </button>
                      <button onClick={handleActualSize} className="bg-blue-500 text-white p-2 rounded-lg text-sm hover:bg-blue-600 transition duration-300 shadow-md">
                        Tamaño Real
                      </button>
                      <button onClick={handleZoomIn} className="bg-blue-500 text-white p-2 rounded-lg text-sm hover:bg-blue-600 transition duration-300 shadow-md">
                        Zoom In (+)
                      </button>
                      <button onClick={handleZoomOut} className="bg-blue-500 text-white p-2 rounded-lg text-sm hover:bg-blue-600 transition duration-300 shadow-md">
                        Zoom Out (-)
                      </button>
                      <button onClick={toggleFullScreen} className="bg-purple-600 text-white p-2 rounded-lg text-sm hover:bg-purple-700 transition duration-300 shadow-md">
                        {isFullScreen ? 'Salir Pantalla Completa' : 'Pantalla Completa'}
                      </button>
                    </div>
                  )}

                  <div className={`flex flex-col items-center overflow-auto border border-gray-300 rounded-lg shadow-inner ${isFullScreen ? 'flex-grow h-full border-none rounded-none' : 'max-h-[80vh]'}`}>
                    <Document
                      file={selectedEbook.URL_Ebook}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      className="w-full max-w-full"
                    >
                      {numPages && Array.from({ length: Math.min(numPages, pagesRenderedCount) }, (_, i) => ( // Renderiza hasta pagesRenderedCount
                        <Page
                          key={`page_${i + 1}`}
                          pageNumber={i + 1}
                          scale={pdfScale} // Usa la escala calculada
                          inputRef={el => pageRefs.current[i] = el} // Guardar referencia para scroll
                          className="mb-4" // Margen entre páginas
                        />
                      ))}
                    </Document>
                    {/* Botón para cargar más páginas */}
                    {numPages && pagesRenderedCount < numPages && !isFullScreen && (
                      <div className="flex justify-center mt-4 mb-4">
                        <button
                          onClick={loadMorePages}
                          className="bg-green-500 text-white p-3 rounded-lg font-semibold hover:bg-green-600 transition duration-300 shadow-md"
                        >
                          Cargar más páginas ({Math.min(numPages, pagesRenderedCount + PAGE_LOAD_CHUNK_SIZE)} / {numPages})
                        </button>
                      </div>
                    )}
                    {/* Controles de paginación (Anterior/Siguiente) - Ocultos en Fullscreen */}
                    {numPages && !isFullScreen && (
                      <div className="flex justify-center items-center mt-4 space-x-4 flex-wrap bg-gray-50 p-3 rounded-lg shadow-sm">
                        <button
                          onClick={goToPrevPage}
                          disabled={pageNumber <= 1}
                          className="bg-blue-600 text-white p-2 rounded-lg font-semibold hover:bg-blue-700 transition duration-300 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          &larr; Anterior
                        </button>
                        <p className="text-lg text-gray-700 mx-2 font-medium">Página {pageNumber} de {numPages}</p>
                        <button
                          onClick={goToNextPage}
                          disabled={pageNumber >= numPages}
                          className="bg-blue-600 text-white p-2 rounded-lg font-semibold hover:bg-blue-700 transition duration-300 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Siguiente &rarr;
                        </button>
                      </div>
                    )}
                  </div>
                  {message && <p className="mt-4 text-center text-red-500 text-sm">{message}</p>}

                  {/* Advertencia de rendimiento para PDFs grandes - Oculta en Fullscreen */}
                  {!isFullScreen && selectedEbook && selectedEbook.URL_Ebook && (
                      <p className="mt-4 text-center text-orange-600 text-sm font-semibold">
                          Nota: PDFs muy grandes pueden experimentar problemas de rendimiento y estabilidad en dispositivos móviles al desplazarse continuamente. Considere optimizar el PDF o usar un visor externo para archivos de gran tamaño.
                      </p>
                  )}
                </div>
              </div>
            );
          default:
            return null;
        }
      })()}
    </div>
  );
}

export default App;
